import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

// Backend API URL for Emergent Auth
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Dev mode - set to false for production
const DEV_MODE = false;
const DEV_USER = {
  id: 'dev_user_123',
  email: 'developer@aibl.money',
  walletAddress: '0x742d35Cc6634C0532925a3b844Bc9e7595f2bD18',
  displayName: 'Dev User'
};
const DEV_AGENTS = [
  { 
    id: 'agent_1', 
    name: 'TradingBot', 
    status: 'active', 
    autonomyLevel: 2,
    wallets: [
      { 
        id: 'wallet_1',
        address: '0x76D0EaAb0693b69CcFcd1234567890abcdef1234', 
        chain: 'base', 
        type: 'SMART_ACCOUNT',
        isDefault: true
      }
    ],
    pluginConfigs: [
      { plugin: 'swap', enabled: true, config: { maxSlippage: 1 } },
      { plugin: 'transfer', enabled: true, config: {} },
      { plugin: 'wrap', enabled: false, config: {} },
      { plugin: 'buy_tokens', enabled: false, config: {} },
      { plugin: 'bridge', enabled: false, config: {} },
    ],
    spendingLimits: {
      maxPerTx: '100000000000000000',  // 0.1 ETH in wei
      dailyLimit: '1000000000000000000' // 1 ETH in wei
    }
  }
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maxAgents] = useState(2);

  // Verify Emergent session with server
  const verifyEmergentSession = async (storedUserJson, sessionToken) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/emergent/me`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const serverUserData = await response.json();
        // Session is valid - use server data
        setUser({
          id: serverUserData.user_id,
          email: serverUserData.email,
          displayName: serverUserData.name,
          picture: serverUserData.picture,
          authProvider: 'emergent_google'
        });
        setToken(sessionToken);
        setAgents([]);
      } else {
        // Session invalid/expired - clear stored data
        console.warn('Emergent session invalid, clearing stored data');
        localStorage.removeItem('emergent_user');
        localStorage.removeItem('emergent_session_token');
      }
    } catch (err) {
      console.error('Failed to verify Emergent session:', err);
      // On network error, fall back to stored data temporarily
      try {
        const userData = JSON.parse(storedUserJson);
        setUser({
          id: userData.user_id,
          email: userData.email,
          displayName: userData.name,
          picture: userData.picture,
          authProvider: 'emergent_google'
        });
        setToken(sessionToken);
        setAgents([]);
      } catch (e) {
        localStorage.removeItem('emergent_user');
        localStorage.removeItem('emergent_session_token');
      }
    }
    setLoading(false);
  };

  // Load stored session on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Check for Emergent auth session first and VALIDATE with server
    const emergentUser = localStorage.getItem('emergent_user');
    const emergentToken = localStorage.getItem('emergent_session_token');
    
    if (emergentUser && emergentToken) {
      // Validate session with server before trusting localStorage
      verifyEmergentSession(emergentUser, emergentToken);
      return;
    }
    
    const storedToken = localStorage.getItem('aibl_token');
    if (storedToken) {
      if (DEV_MODE) {
        // Dev mode bypass
        setToken(storedToken);
        setUser(DEV_USER);
        setAgents(DEV_AGENTS);
        setSelectedAgent(DEV_AGENTS[0]);
        setLoading(false);
      } else {
        // Verify token is still valid before using it
        verifyAndLoadSession(storedToken);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Verify token and load session data
  const verifyAndLoadSession = async (storedToken) => {
    try {
      const res = await api.getMe(storedToken);
      if (res.success && res.data?.user) {
        // Check if this is a different user than expected (stale session)
        const storedUserId = localStorage.getItem('aibl_user_id');
        const currentUserId = res.data.user.id || res.data.user.walletAddress || res.data.user.email;
        
        if (storedUserId && storedUserId !== currentUserId) {
          // User mismatch - clear stale session
          console.warn('User mismatch detected, clearing stale session');
          logout();
          return;
        }
        
        // Store current user ID for future checks
        localStorage.setItem('aibl_user_id', currentUserId);
        
        setToken(storedToken);
        setUser(res.data.user);
        setAgents(res.data.agents || []);
        if (res.data.agents?.length > 0) {
          setSelectedAgent(res.data.agents[0]);
        }
      } else {
        // Token invalid or expired
        console.warn('Token verification failed, logging out');
        logout();
      }
    } catch (err) {
      console.error('Session verification failed:', err);
      logout();
    }
    setLoading(false);
  };

  // Fetch user data and agents
  const fetchUserData = async (authToken) => {
    try {
      const res = await api.getMe(authToken);
      if (res.success) {
        // Store user ID for session validation
        const userId = res.data.user.id || res.data.user.walletAddress || res.data.user.email;
        localStorage.setItem('aibl_user_id', userId);
        
        setUser(res.data.user);
        setAgents(res.data.agents || []);
        if (res.data.agents?.length > 0) {
          setSelectedAgent(res.data.agents[0]);
        }
      } else {
        logout();
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      logout();
    }
    setLoading(false);
  };

  // Login with Google
  const loginWithGoogle = useCallback(async (idToken) => {
    try {
      const res = await api.googleLogin(idToken);
      if (res.success) {
        const { token: authToken, user: userData } = res.data;
        localStorage.setItem('aibl_token', authToken);
        setToken(authToken);
        setUser(userData);
        await fetchUserData(authToken);
        return { success: true };
      }
      return { success: false, error: res.error?.message || 'Google login failed' };
    } catch (err) {
      console.error('Google login error:', err);
      return { success: false, error: err.message || 'Connection failed' };
    }
  }, []);

  // Login with email
  const loginWithEmail = useCallback(async (email) => {
    try {
      const res = await api.emailLogin(email);
      if (res.success) {
        const { token: authToken, user: userData } = res.data;
        localStorage.setItem('aibl_token', authToken);
        setToken(authToken);
        setUser(userData);
        await fetchUserData(authToken);
        return { success: true };
      }
      return { success: false, error: res.error?.message || 'Email login failed' };
    } catch (err) {
      return { success: false, error: 'Connection failed' };
    }
  }, []);

  // Login with wallet
  const loginWithWallet = useCallback(async (walletAddress, signature, nonce) => {
    try {
      const res = await api.login(walletAddress, signature, nonce);
      if (res.success) {
        const { token: authToken, user: userData } = res.data;
        localStorage.setItem('aibl_token', authToken);
        setToken(authToken);
        setUser(userData);
        await fetchUserData(authToken);
        return { success: true };
      }
      return { 
        success: false, 
        error: res.error?.message || 'Wallet login failed',
        errorCode: res.error?.code
      };
    } catch (err) {
      console.error('Wallet login error:', err);
      return { success: false, error: err.message || 'Connection failed' };
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    // Clear AIBL auth
    localStorage.removeItem('aibl_token');
    localStorage.removeItem('aibl_user_id');
    // Clear Emergent auth
    localStorage.removeItem('emergent_session_token');
    localStorage.removeItem('emergent_user');
    // Reset state
    setToken(null);
    setUser(null);
    setAgents([]);
    setSelectedAgent(null);
  }, []);

  // Refresh agents list
  const refreshAgents = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.getAgents(token);
      if (res.success) {
        setAgents(res.data.agents || []);
      }
    } catch (err) {
      console.error('Failed to refresh agents:', err);
    }
  }, [token]);

  // Create new agent
  const createAgent = useCallback(async (agentData) => {
    if (!token) return { success: false, error: 'Not authenticated' };
    if (agents.length >= maxAgents) {
      return { success: false, error: `Maximum ${maxAgents} agents per account` };
    }
    try {
      const res = await api.createAgent(token, agentData);
      if (res.success) {
        await refreshAgents();
        return { 
          success: true, 
          agent: res.data?.agent,
          wallet: res.data?.wallet,
          apiKey: res.data?.apiKey
        };
      }
      return { success: false, error: res.error?.message || 'Failed to create agent' };
    } catch (err) {
      return { success: false, error: 'Failed to create agent' };
    }
  }, [token, agents.length, maxAgents, refreshAgents]);

  const value = {
    user,
    token,
    agents,
    selectedAgent,
    setSelectedAgent,
    loading,
    maxAgents,
    isAuthenticated: !!user && !!token,
    hasAgents: agents.length > 0,
    canCreateAgent: agents.length < maxAgents,
    loginWithGoogle,
    loginWithEmail,
    loginWithWallet,
    logout,
    refreshAgents,
    createAgent,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
